import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../../context/LoadingContext";
import {
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  fetchData,
  postQuery,
} from "../../../Requirement/services/RequirementServices";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";

const RequirementForm = ({
  formMode,
  setFormMode,
  selectedRequirement,
  setIsFormVisible,
  productList,
  allProductMaster,
  activeUser,
  buyerInfo,
  totalItems,
  setModal,
  searchRequirements,
  searchValues,
  navigate,
}) => {
  const { setLoading } = useLoading();
  const [formValues, setFormValues] = useState({
    product: "",
    grade: "",
    origin: "",
    destination: "",
    deliveryPeriod: "",
    quantity: "",
    incoterms: "",
    paymentTerms: "",
    packaging: "",
    deliveryPort: "",
    deliveryPeriodFrom: null,
    deliveryPeriodTill: null,
    deliveryPeriodTillRange: "",
    validUntil: null,
    validUntilRange: "",
  });
  const [productGradeList, setProductGradeList] = useState([]);
  const [formProduct, setFormProduct] = useState([]);
  const [countrymaster, setCountryMaster] = useState([]);
  const [paymentTermMaster, setPaymentTermMaster] = useState([]);
  const [incotermsMaster, setIncotermsMaster] = useState([]);
  const [formPortList, setFormPortList] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const packagingOptions = [
    { id: "1", name: "Sack" },
    { id: "2", name: "Big Bag" },
  ];

  const loadProductGrades = useCallback(
    async (productName) => {
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName, Status: "ACTIVE" },
          }),
        ]);
        if (allGrades.items.length > 0) {
          setProductGradeList(allGrades.items);
        } else {
          setProductGradeList([]);
        }
      } catch (error) {
        console.error("Error loading product grades: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const loadFormProductInfo = useCallback(async (productName, grade) => {
    try {
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { ProductName: productName, Grade: grade },
      });
      if (response.items.length > 0) {
        setFormProduct(response.items[0] || []);
      } else {
        setFormProduct([]);
      }
    } catch (error) {
      console.error("Error loading form product info:", error);
      setFormProduct([]);
    }
  }, []);

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Country" },
        }),
      ]);
      if (allCountries.items.length > 0) {
        setCountryMaster(allCountries.items);
      } else {
        setCountryMaster([]);
      }
    } catch (error) {
      console.error("Error loading countries: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadPaymentTerms = useCallback(async () => {
    setLoading(true);
    try {
      const [allPaymentTerms] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "paymentTerms" },
        }),
      ]);
      if (allPaymentTerms.items.length > 0) {
        setPaymentTermMaster(allPaymentTerms.items);
      } else {
        setPaymentTermMaster([]);
      }
    } catch (error) {
      console.error("Error loading paymentTerms: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadIncoterms = useCallback(async () => {
    setLoading(true);
    try {
      const [allIncoterms] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Incoterms" },
        }),
      ]);
      if (allIncoterms.items.length > 0) {
        setIncotermsMaster(allIncoterms.items);
      } else {
        setIncotermsMaster([]);
      }
    } catch (error) {
      console.error("Error loading Incoterms: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadFormPorts = useCallback(
    async (country) => {
      setLoading(true);
      try {
        const [allPorts] = await Promise.all([
          fetchData({
            target: "listDropDownOptions",
            userOrder: "Value",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { Type: "port", Subtype: country },
          }),
        ]);
        if (allPorts.items.length > 0) {
          setFormPortList(allPorts.items);
        } else {
          setFormPortList([]);
        }
      } catch (error) {
        console.error("Error loading ports: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const validateForm = useCallback(() => {
    const newErrors = {};
    const today = dayjs().startOf("day");

    if (!formValues.product) newErrors.product = "Product is required";
    if (!formValues.grade) newErrors.grade = "Grade is required";
    if (!formValues.quantity) {
      newErrors.quantity = "Quantity is required";
    } else if (isNaN(formValues.quantity) || Number(formValues.quantity) <= 0) {
      newErrors.quantity = "Quantity must be a positive number";
    }
    if (!formValues.origin) newErrors.origin = "Origin country is required";
    if (!formValues.destination)
      newErrors.destination = "Destination country is required";
    if (!formValues.deliveryPort)
      newErrors.deliveryPort = "Delivery port is required";
    if (!formValues.packaging) newErrors.packaging = "Packaging is required";
    if (!formValues.incoterms) newErrors.incoterms = "Incoterms is required";
    if (!formValues.paymentTerms)
      newErrors.paymentTerms = "Payment terms is required";
    if (!formValues.deliveryPeriodFrom) {
      newErrors.deliveryPeriodFrom = "Delivery start date is required";
    } else if (!dayjs(formValues.deliveryPeriodFrom).isValid()) {
      newErrors.deliveryPeriodFrom = "Invalid start date format";
    } else if (dayjs(formValues.deliveryPeriodFrom).isBefore(today)) {
      newErrors.deliveryPeriodFrom = "Start date cannot be in the past";
    }
    if (!formValues.deliveryPeriodTill) {
      newErrors.deliveryPeriodTill = "Delivery end date is required";
    } else if (!dayjs(formValues.deliveryPeriodTill).isValid()) {
      newErrors.deliveryPeriodTill = "Invalid end date format";
    } else if (
      formValues.deliveryPeriodFrom &&
      dayjs(formValues.deliveryPeriodTill).isBefore(
        dayjs(formValues.deliveryPeriodFrom)
      )
    ) {
      newErrors.deliveryPeriodTill = "End date must be after start date";
    }
    if (!formValues.validUntil) {
      newErrors.validUntil = "Valid until date is required";
    } else if (!dayjs(formValues.validUntil).isValid()) {
      newErrors.validUntil = "Invalid valid until date format";
    } else if (dayjs(formValues.validUntil).isBefore(today)) {
      newErrors.validUntil = "Valid until date must be in the future";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formValues]);

  const handleSubmit = async () => {
    if (!validateForm()) {
      setModal({
        show: true,
        type: "error",
        title: "Validation Error",
        message: "Please correct the errors in the form before submitting.",
        autoClose: false,
      });
      return;
    }

    let insertedRequirementsID = null;
    const insertedPaymentConditionsIDs = [];
    setSubmitted(true);
    setLoading(true);

    try {
      const packageingValue = { item: formValues.packaging, size: "" };
      const curreDate = new Date();
      const reqNoDate = dayjs(curreDate).format("YYYYMMDD");
      const reqCount = totalItems + 1;
      const reqNumber = 1 + reqNoDate + reqCount;

      const requestedQuantity = parseInt(formValues.quantity || 0);
      const requestedProductID = formProduct.ProductID || "";
      const category = formProduct.ProductCategoryID || "";
      const productType = "";
      const priceOffered = "0.00";
      const deliveryPeriodTill = formValues.deliveryPeriodTill || "";
      const deliveryPeriodFrom = formValues.deliveryPeriodFrom || "";
      const validUntil = formValues.validUntil || "";
      const incoterm = formValues.incoterms || "";
      const destination = formValues.destination || "";
      const deliveryPort = formValues.deliveryPort || "";
      const locations = destination + "~" + deliveryPort;
      const originCountry = formValues.origin || "";

      let gpEmail = buyerInfo.length > 0 ? buyerInfo[0].GPEmailID || "" : "";
      if (gpEmail === "") gpEmail = activeUser;
      let recordStatus = "Approved";

      if (formMode === "edit") {
        const body = {
          target: "updateRequirements",
          requirementsInput: {
            RequirementsID: selectedRequirement.RequirementsID,
            RequirementNumber: selectedRequirement.RequirementNumber,
            GPEmailID: gpEmail,
            BuyerEmailID: activeUser,
            CreatedByEmailID: activeUser,
            ProductID: requestedProductID,
            Quantity: requestedQuantity,
            Price: priceOffered,
            DeliveryDate: deliveryPeriodTill,
            RequirementDate: dayjs(curreDate).format("YYYY-MM-DD"),
            MarketPlacement: "No",
            ValidUntil: validUntil,
            Incoterm: incoterm,
            DeliveryLocation: locations,
            DeliveryPeriodFrom: deliveryPeriodFrom,
            DeliveryPeriodTill: deliveryPeriodTill,
            OriginCountry: originCountry,
            ProductPackaging: packageingValue,
            RequestedQuantity: requestedQuantity,
            PriceOffered: priceOffered,
            Status: recordStatus,
            CategoryType: "requirement",
            Deleted: 0,
          },
        };

        await postQuery(body);
        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: "Requirement updated successfully.",
          autoClose: false,
          nextAction: () => {
            navigate("/global-partner/requirement");
            searchRequirements(searchValues);
          },
        });
      } else if (formMode === "add") {
        const body = {
          target: "createRequirements",
          ExtraInfo: {
            ProductName: formProduct?.ProductName,
            Grade: formProduct?.Grade,
          },
          requirementsInput: {
            RequirementsID: uuidv4(),
            RequirementNumber: reqNumber,
            GPEmailID: gpEmail,
            BuyerEmailID: activeUser,
            CreatedByEmailID: activeUser,
            ProductID: requestedProductID,
            Quantity: requestedQuantity,
            Price: priceOffered,
            DeliveryDate: deliveryPeriodTill,
            RequirementDate: dayjs(curreDate).format("YYYY-MM-DD"),
            MarketPlacement: "No",
            ValidUntil: validUntil,
            Incoterm: incoterm,
            DeliveryLocation: locations,
            DeliveryPeriodFrom: deliveryPeriodFrom,
            DeliveryPeriodTill: deliveryPeriodTill,
            OriginCountry: originCountry,
            ProductPackaging: packageingValue,
            RequestedQuantity: requestedQuantity,
            PriceOffered: priceOffered,
            Status: recordStatus,
            CategoryType: "requirement",
            Deleted: 0,
          },
        };

        const result = await postQuery(body);
        if (!result || !result.items)
          throw new Error("Failed to create requirement.");
        insertedRequirementsID = result.items.RequirementsID;

        const bodyProductInfo = {
          target: "createRequirementProductInfo",
          requirementProductInfoInput: {
            RequirementProductInfoID: uuidv4(),
            RequirementsID: insertedRequirementsID,
            Category: category,
            ProductID: requestedProductID,
            ProductTypeID: productType,
            Deleted: 0,
          },
        };

        await postQuery(bodyProductInfo);

        const paymentConditionsID = uuidv4();
        const bodyPC = {
          target: "createPaymentConditions",
          paymentConditionsInput: {
            PaymentConditionsID: paymentConditionsID,
            RequirementsID: insertedRequirementsID,
            PaymentOptions: formValues.paymentTerms || "",
            TermsConditionsFilePath: "",
            OthersFilePath: "",
            Deleted: 0,
          },
        };

        await postQuery(bodyPC);
        insertedPaymentConditionsIDs.push(paymentConditionsID);

        const bodyRTH = {
          target: "createRequirementTxnHistory",
          requirementTxnHistoryInput: {
            RequirementTxnHistoryID: uuidv4(),
            RequirementsID: insertedRequirementsID,
            ActionedByEmail: activeUser,
            Status: recordStatus,
            Deleted: 0,
          },
        };
        await postQuery(bodyRTH);

        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: "Requirement created successfully.",
          autoClose: false,
          nextAction: () => {
            navigate("/global-partner/requirement");
            searchRequirements(searchValues);
          },
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      try {
        await Promise.all(
          insertedPaymentConditionsIDs.map(async (paymentConditionsID) => {
            const bodyDelPC = {
              target: "deletePaymentConditions",
              PaymentConditionsID: paymentConditionsID,
            };
            await postQuery(bodyDelPC);
          })
        );
        if (insertedRequirementsID) {
          const bodyDelR = {
            target: "deleteRequirements",
            RequirementsID: insertedRequirementsID,
          };
          await postQuery(bodyDelR);
        }
      } catch (rollbackError) {
        console.error("Rollback error:", rollbackError);
        setModal({
          show: true,
          type: "error",
          title: "Rollback Error!",
          message:
            "Failed to clean up data after error. Please contact support.",
          autoClose: false,
        });
        setLoading(false);
        setSubmitted(false);
        return;
      }
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || "Failed to submit requirement.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
      setSubmitted(false);
      setFormValues({
        product: "",
        grade: "",
        origin: "",
        destination: "",
        deliveryPeriod: "",
        quantity: "",
        incoterms: "",
        paymentTerms: "",
        packaging: "",
        deliveryPort: "",
        deliveryPeriodFrom: null,
        deliveryPeriodTill: null,
        deliveryPeriodTillRange: "",
        validUntil: null,
        validUntilRange: "",
      });
      setIsFormVisible(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const parseProductPackaging = (packagingString) => {
    try {
      const cleanedString = packagingString.replace(/[{}]/g, "");
      const keyValuePairs = cleanedString.split(", ").reduce((acc, pair) => {
        const [key, value] = pair.split("=");
        acc[key] = value;
        return acc;
      }, {});
      return { item: keyValuePairs.item || "", size: keyValuePairs.size || "" };
    } catch (error) {
      console.error("Error parsing ProductPackaging:", error);
      return { item: "", size: "" };
    }
  };

  const loadPaymentConditions = useCallback(async (requirementsID) => {
    try {
      const response = await fetchData({
        target: "listPaymentConditions",
        userOrder: "PaymentConditionsID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { RequirementsID: requirementsID },
      });
      return response.items || [];
    } catch (error) {
      console.error("Error loading payment conditions:", error);
      return [];
    }
  }, []);

  const handleEdit = () => {
    setFormMode("edit");
  };

  useEffect(() => {
    loadCountries();
    loadPaymentTerms();
    loadIncoterms();
    if (formMode === "edit" || formMode === "view") {
      if (selectedRequirement) {
        const packaging = parseProductPackaging(
          selectedRequirement.ProductPackaging || ""
        );
        const product = allProductMaster.find(
          (p) => p.ProductID === selectedRequirement.ProductID
        );
        if (product) {
          loadProductGrades(product.ProductName);
          setFormValues((prev) => ({
            ...prev,
            product: product.ProductName,
            grade: product.Grade,
          }));
          loadFormProductInfo(product.ProductName, product.Grade);
        }
        loadPaymentConditions(selectedRequirement.RequirementsID).then(
          (paymentConditions) => {
            const paymentOptions = paymentConditions[0]?.PaymentOptions || "";
            const [destination, deliveryPort] =
              selectedRequirement.DeliveryLocation.split("~");
            loadFormPorts(destination);
            setFormValues((prev) => ({
              ...prev,
              origin: selectedRequirement.OriginCountry || "",
              destination: destination || "",
              deliveryPort: deliveryPort || "",
              quantity: selectedRequirement.Quantity.toString() || "",
              incoterms: selectedRequirement.Incoterm || "",
              paymentTerms: paymentOptions,
              packaging: packaging.item || "",
              deliveryPeriodFrom: selectedRequirement.DeliveryPeriodFrom
                ? dayjs(selectedRequirement.DeliveryPeriodFrom).format(
                    "YYYY-MM-DD"
                  )
                : null,
              deliveryPeriodTill: selectedRequirement.DeliveryPeriodTill
                ? dayjs(selectedRequirement.DeliveryPeriodTill).format(
                    "YYYY-MM-DD"
                  )
                : null,
              validUntil: selectedRequirement.ValidUntil
                ? dayjs(selectedRequirement.ValidUntil).format("YYYY-MM-DD")
                : null,
              deliveryPeriodTillRange: "custom",
              validUntilRange: "custom",
            }));
          }
        );
      }
    } else if (formMode === "add") {
      setFormValues({
        product: "",
        grade: "",
        origin: "",
        destination: "",
        deliveryPeriod: "",
        quantity: "",
        incoterms: "",
        paymentTerms: "",
        packaging: "",
        deliveryPort: "",
        deliveryPeriodFrom: null,
        deliveryPeriodTill: null,
        deliveryPeriodTillRange: "",
        validUntil: null,
        validUntilRange: "",
      });
      setProductGradeList([]);
      setFormProduct([]);
      setFormPortList([]);
    }
  }, [
    formMode,
    selectedRequirement,
    loadCountries,
    loadPaymentTerms,
    loadIncoterms,
    loadProductGrades,
    loadFormProductInfo,
    allProductMaster,
  ]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="form-section" style={{ marginTop: "20px" }}>
        <div className="table-header">
          <div className="header-text">
            <div className="head-text">
              {formMode === "add" && "Add Requirement"}
              {formMode === "edit" && "Edit Requirement"}
              {formMode === "view" && "View Requirement"}
            </div>
          </div>
          <div className="head-btn">
            {formMode === "view" && (
              <Button className="btn btn-question " onClick={handleEdit}>
                Edit
              </Button>
            )}
            {(formMode === "add" || formMode === "edit") && (
              <Button
                className="btn btn-question "
                onClick={handleSubmit}
                disabled={submitted}
                sx={{
                  backgroundColor: "#de6b14",
                  color: "#fff",
                  marginRight: "5px",
                }}
              >
                {submitted
                  ? "Submitting..."
                  : formMode === "edit"
                  ? "Update"
                  : "Submit"}
              </Button>
            )}
            <Button
              className="btn btn-question "
              onClick={() => setIsFormVisible(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
        <div className="row" style={{ marginTop: "20px" }}>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.product}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Product *</Typography>
                <Select
                  name="product"
                  value={formValues.product}
                  onChange={(e) => {
                    handleFormChange(e);
                    loadProductGrades(e.target.value);
                    setErrors((prev) => ({ ...prev, product: "" }));
                  }}
                  displayEmpty
                  sx={{ backgroundColor: "white" }}
                  inputProps={{ "aria-label": "Select Product" }}
                >
                  <MenuItem value="" disabled>
                    Select Product
                  </MenuItem>
                  {productList.map((p) => (
                    <MenuItem key={p.ProductID} value={p.ProductName}>
                      {p.ProductName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.product && (
                  <FormHelperText>{errors.product}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.grade}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Grade *</Typography>
                <Select
                  name="grade"
                  value={formValues.grade}
                  onChange={(e) => {
                    handleFormChange(e);
                    loadFormProductInfo(formValues.product, e.target.value);
                    setErrors((prev) => ({ ...prev, grade: "" }));
                  }}
                  displayEmpty
                  sx={{ backgroundColor: "white" }}
                  inputProps={{ "aria-label": "Select Grade" }}
                >
                  <MenuItem value="" disabled>
                    Select Grade
                  </MenuItem>
                  {productGradeList.map((p) => (
                    <MenuItem key={p.ProductID} value={p.Grade}>
                      {p.Grade}
                    </MenuItem>
                  ))}
                </Select>
                {errors.grade && (
                  <FormHelperText>{errors.grade}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.origin}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Origin *</Typography>
                <Select
                  name="origin"
                  value={formValues.origin}
                  onChange={(e) => {
                    handleFormChange(e);
                    setErrors((prev) => ({ ...prev, origin: "" }));
                  }}
                  displayEmpty
                  sx={{ backgroundColor: "white" }}
                  inputProps={{ "aria-label": "Select Origin" }}
                >
                  <MenuItem value="" disabled>
                    Select Origin
                  </MenuItem>
                  {countrymaster.map((p) => (
                    <MenuItem key={p.ID} value={p.Value}>
                      {p.Value}
                    </MenuItem>
                  ))}
                </Select>
                {errors.origin && (
                  <FormHelperText>{errors.origin}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.destination}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Destination *</Typography>
                <Select
                  name="destination"
                  value={formValues.destination}
                  onChange={(e) => {
                    handleFormChange(e);
                    loadFormPorts(e.target.value);
                    setErrors((prev) => ({ ...prev, destination: "" }));
                  }}
                  displayEmpty
                  sx={{ backgroundColor: "white" }}
                  inputProps={{ "aria-label": "Select Destination" }}
                >
                  <MenuItem value="" disabled>
                    Select Destination
                  </MenuItem>
                  {countrymaster.map((p) => (
                    <MenuItem key={p.ID} value={p.Value}>
                      {p.Value}
                    </MenuItem>
                  ))}
                </Select>
                {errors.destination && (
                  <FormHelperText>{errors.destination}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <Typography>Quantity (MT) *</Typography>
              <TextField
                type="number"
                name="quantity"
                value={formValues.quantity}
                onChange={(e) => {
                  handleFormChange(e);
                  setErrors((prev) => ({ ...prev, quantity: "" }));
                }}
                error={!!errors.quantity}
                helperText={errors.quantity}
                sx={{ backgroundColor: "white", width: "100%" }}
                placeholder="Enter Quantity"
                disabled={formMode === "view"}
              />
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.incoterms}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Inco Terms</Typography>
                <Select
                  name="incoterms"
                  value={formValues.incoterms}
                  onChange={(e) => {
                    handleFormChange(e);
                    setErrors((prev) => ({ ...prev, incoterms: "" }));
                  }}
                  displayEmpty
                  sx={{ backgroundColor: "white" }}
                  inputProps={{ "aria-label": "Select Inco Terms" }}
                >
                  <MenuItem value="" disabled>
                    Select Inco Terms
                  </MenuItem>
                  {incotermsMaster.map((incotermOption, index) => (
                    <MenuItem key={index} value={incotermOption.Value}>
                      {incotermOption.Value}
                    </MenuItem>
                  ))}
                </Select>
                {errors.incoterms && (
                  <FormHelperText>{errors.incoterms}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.paymentTerms}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Payment Terms</Typography>
                <Select
                  name="paymentTerms"
                  value={formValues.paymentTerms}
                  onChange={(e) => {
                    handleFormChange(e);
                    setErrors((prev) => ({ ...prev, paymentTerms: "" }));
                  }}
                  displayEmpty
                  sx={{ backgroundColor: "white" }}
                  inputProps={{ "aria-label": "Select Payment Terms" }}
                >
                  <MenuItem value="" disabled>
                    Select Payment Terms
                  </MenuItem>
                  {paymentTermMaster.map((option, index) => (
                    <MenuItem key={index} value={option.Value}>
                      {option.Value}
                    </MenuItem>
                  ))}
                </Select>
                {errors.paymentTerms && (
                  <FormHelperText>{errors.paymentTerms}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.packaging}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Packaging</Typography>
                <Select
                  name="packaging"
                  value={formValues.packaging}
                  onChange={(e) => {
                    handleFormChange(e);
                    setErrors((prev) => ({ ...prev, packaging: "" }));
                  }}
                  displayEmpty
                  sx={{ backgroundColor: "white" }}
                  inputProps={{ "aria-label": "Select Packaging" }}
                >
                  <MenuItem value="" disabled>
                    Select Packaging
                  </MenuItem>
                  {packagingOptions.map((option) => (
                    <MenuItem key={option.id} value={option.name}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.packaging && (
                  <FormHelperText>{errors.packaging}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.deliveryPort}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Delivery Port</Typography>
                <Select
                  name="deliveryPort"
                  value={formValues.deliveryPort}
                  onChange={(e) => {
                    handleFormChange(e);
                    setErrors((prev) => ({ ...prev, deliveryPort: "" }));
                  }}
                  displayEmpty
                  sx={{ backgroundColor: "white" }}
                  inputProps={{ "aria-label": "Select Delivery Port" }}
                >
                  <MenuItem value="" disabled>
                    Select Delivery Port
                  </MenuItem>
                  {formPortList.length > 0 ? (
                    formPortList.map((option, index) => (
                      <MenuItem key={index} value={option.Value}>
                        {option.Value}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="ACTIVE">Loading...</MenuItem>
                  )}
                </Select>
                {errors.deliveryPort && (
                  <FormHelperText>{errors.deliveryPort}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.deliveryPeriodFrom}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Delivery (Start) *</Typography>
                <DatePicker
                  value={
                    formValues.deliveryPeriodFrom
                      ? dayjs(formValues.deliveryPeriodFrom)
                      : null
                  }
                  onChange={(date) => {
                    setFormValues((prev) => ({
                      ...prev,
                      deliveryPeriodFrom: date
                        ? date.format("YYYY-MM-DD")
                        : null,
                      deliveryPeriodTillRange: date
                        ? "custom"
                        : prev.deliveryPeriodTillRange,
                    }));
                    setErrors((prev) => ({ ...prev, deliveryPeriodFrom: "" }));
                  }}
                  minDate={dayjs()}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      sx: { backgroundColor: "white", width: "100%" },
                      placeholder: "DD-MM-YYYY",
                      error: !!errors.deliveryPeriodFrom,
                      helperText: errors.deliveryPeriodFrom,
                    },
                  }}
                />
              </FormControl>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.deliveryPeriodTill}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Delivery (End) *</Typography>
                <DatePicker
                  value={
                    formValues.deliveryPeriodTill
                      ? dayjs(formValues.deliveryPeriodTill)
                      : null
                  }
                  onChange={(date) => {
                    setFormValues((prev) => ({
                      ...prev,
                      deliveryPeriodTill: date
                        ? date.format("YYYY-MM-DD")
                        : null,
                      deliveryPeriodTillRange: date
                        ? "custom"
                        : prev.deliveryPeriodTillRange,
                    }));
                    setErrors((prev) => ({ ...prev, deliveryPeriodTill: "" }));
                  }}
                  minDate={
                    formValues.deliveryPeriodFrom
                      ? dayjs(formValues.deliveryPeriodFrom)
                      : dayjs()
                  }
                  format="DD-MM-YYYY"
                  disabled={!formValues.deliveryPeriodFrom}
                  slotProps={{
                    textField: {
                      sx: { backgroundColor: "white", width: "100%" },
                      placeholder: "DD-MM-YYYY",
                      error: !!errors.deliveryPeriodTill,
                      helperText: errors.deliveryPeriodTill,
                    },
                  }}
                />
              </FormControl>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <FormControl
                sx={{ minWidth: "100%" }}
                error={!!errors.validUntil}
                disabled={formMode === "view"}
              >
                <Typography variant="body2">Valid Until *</Typography>
                <DatePicker
                  value={
                    formValues.validUntil ? dayjs(formValues.validUntil) : null
                  }
                  onChange={(date) => {
                    setFormValues((prev) => ({
                      ...prev,
                      validUntil: date ? date.format("YYYY-MM-DD") : null,
                      validUntilRange: date ? "custom" : prev.validUntilRange,
                    }));
                    setErrors((prev) => ({ ...prev, validUntil: "" }));
                  }}
                  minDate={dayjs()}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      sx: { backgroundColor: "white", width: "100%" },
                      placeholder: "DD-MM-YYYY",
                      error: !!errors.validUntil,
                      helperText: errors.validUntil,
                    },
                  }}
                />
              </FormControl>
            </div>
          </div>
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default RequirementForm;
